import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authApi } from '../../services/api';
import { AuthLayout, Fehlermeldung } from './AuthLayout';

/**
 * Registrierung einer Firma.
 *
 * Legt Mandant und ersten Benutzer an. Standardmaessig ist das Konto sofort
 * freigeschaltet (Backend: REGISTRIERUNG_AUTO); sonst folgt die Bestaetigung
 * der E-Mail, damit niemand ein Konto auf eine fremde Adresse anlegt.
 */
export default function RegisterPage() {
  const navigate = useNavigate();
  const [daten, setDaten] = useState({
    firma_name: '',
    vorname: '',
    nachname: '',
    email: '',
    passwort: '',
  });
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  function feld(name: keyof typeof daten) {
    return {
      value: daten[name],
      onChange: (e: { target: { value: string } }) =>
        setDaten((d) => ({ ...d, [name]: e.target.value })),
    };
  }

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setLaeuft(true);
    try {
      const antwort = await authApi.registrieren(daten);
      // Vorgabe: sofort freigeschaltet, dann gibt es nichts zu bestaetigen.
      navigate(
        antwort.freigeschaltet ? '/login' : `/email-bestaetigen?benutzer=${antwort.benutzer_id}`,
        { replace: true }
      );
    } catch (err) {
      setFehler(err);
    } finally {
      setLaeuft(false);
    }
  }

  const eingabe =
    'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

  return (
    <AuthLayout
      titel="Firma registrieren"
      untertitel="Sie legen damit ein eigenes, von allen anderen getrenntes Konto an."
      fusszeile={
        <>
          Bereits registriert?{' '}
          <Link to="/login" className="font-medium text-primary-600 hover:underline">
            Anmelden
          </Link>
        </>
      }
    >
      <Fehlermeldung fehler={fehler} />

      <form onSubmit={absenden} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Firmenname</label>
          <input required className={eingabe} {...feld('firma_name')} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Vorname</label>
            <input className={eingabe} {...feld('vorname')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nachname</label>
            <input required className={eingabe} {...feld('nachname')} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">E-Mail</label>
          <input type="email" required autoComplete="username" className={eingabe} {...feld('email')} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Passwort</label>
          <input
            type="password"
            required
            minLength={12}
            autoComplete="new-password"
            className={eingabe}
            {...feld('passwort')}
          />
          <p className="mt-1 text-xs text-gray-500">
            Mindestens 12&nbsp;Zeichen. Eine Merksatz-Kombination aus mehreren
            Wörtern ist sicherer und leichter zu behalten als ein kurzes
            Passwort mit Sonderzeichen.
          </p>
        </div>

        <button
          type="submit"
          disabled={laeuft}
          className="flex w-full items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
        >
          {laeuft && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Registrieren
        </button>
      </form>
    </AuthLayout>
  );
}
